/**
 * This logic written specifically for Radar device types
 * Radar currently can be configured only using Synthetic APIs
 * See more here: https://github.com/peoplepower/peoplepower-docs/blob/master/synthetic_apis/radar.md#set-the-room-boundaries
 */
import radarTemplate from './radar.html';

export default angular
  .module('main.devices.edit.tabs.radar', [])

  .directive('editDeviceRadarTab', ($filter) => {
    const $t = $filter('translate');
    return {
      restrict: 'E',
      template: radarTemplate,
      scope: {},
      controller: (
        $scope,
        $q,
        $filter,
        utils,
        EditDeviceDataProvider,
        I18nService,
        ToastsService
      ) => {
        $scope.locale = I18nService.getLocale();
        $scope.dataProvider = EditDeviceDataProvider.instance;
        $scope.isNewSubregion = false;
        const userNameFilter = $filter('userName');

        const subregionMinSize = 0.05;

        // Mapping to bot properties
        const PROPS_MAPPING = [
          {
            id: 'uniqueId',
            botProperty: 'unique_id'
          },
          {
            id: 'subregionName',
            botProperty: 'name'
          },
          {
            id: 'subregionType',
            botProperty: 'context_id',
            options: true
          },
          {
            id: 'xMin',
            botProperty: 'x_min_meters'
          },
          {
            id: 'xMax',
            botProperty: 'x_max_meters'
          },
          {
            id: 'yMin',
            botProperty: 'y_min_meters'
          },
          {
            id: 'yMax',
            botProperty: 'y_max_meters'
          }
        ];

        $scope.options = [
          {
            id: 'detect_falls',
            title: $t('regions.subregion.options.fall'),
            description: $t('regions.subregion.options.fallDesc'),
            icon: 'bell-on',
            value: false,
            editProperty: 'edit_falls',
            editable: false
          },
          {
            id: 'detect_presence',
            title: $t('regions.subregion.options.presence'),
            description: $t('regions.subregion.options.presenceDesc'),
            icon: 'users',
            value: false,
            editProperty: 'edit_presence',
            editable: false
          },
        ];

        angular.extend($scope, {
          onCancelButtonClick: onCancelButtonClick,
          switchOption: switchOption,
          onSaveButtonClick: onSaveButtonClick,
          checkEmpty: checkEmpty,
          convertToOppositeUnits: convertToOppositeUnits,
          onSubregionSchemaChange: onSubregionSchemaChange
        });

        init();

        function init() {
          $scope.loading = true;

          initFormRules();

          if (!$scope.dataProvider.subregionToEdit) {
            $scope.isNewSubregion = true;
            $scope.subregion = {
              xMin: 0,
              xMax: 0,
              yMin: 0,
              yMax: 0,
            };
          }

          $q.all([
            $scope.dataProvider.deviceResource.init(),
            $scope.dataProvider.botDrivenRegionsResource.init(),
            $scope.dataProvider.locationStateRegionBehaviorsResource.init(),
            $scope.dataProvider.locationStateRegionsResource.init(),
            $scope.dataProvider.locationStateBoundariesResource.init(),
            $scope.dataProvider.locationUsersResource.init(),
            $scope.dataProvider.userSystemOfMeasurementResource.init(),
          ])
            .then(() => {
              $scope.systemOfMeasurement = $scope.dataProvider.userSystemOfMeasurementResource.value;
              initWatchers();
              $scope.loading = false;
            }, (err) => {
              $scope.editable = false;
              $scope.loading = false;
            });
        }

        function initWatchers() {
          $scope.$watchGroup([
            'dataProvider.botDrivenRegionsResource.value',
            'dataProvider.locationStateBoundariesResource.value',
            'dataProvider.locationStateRegionBehaviorsResource.value',
            'dataProvider.locationStateRegionsResource.value',
            'dataProvider.locationUsersResource.value',
          ], () => {
            initViewModel();
          }, true);

          // Gain default options for selected subregion behavior
          $scope.$watch('selectedSubregionType', (newValue, oldValue) => {
            if (!newValue || oldValue === newValue || (!oldValue && !$scope.isNewSubregion)) { // skip first (initial) change
              return;
            }

            const selectedSubregionType = $scope.selectedSubregionType;
            $scope.options.forEach((option) => {
              option.editable = selectedSubregionType[option.editProperty] || false;
              if (!option.editable) {
                // taking default value for selected subregion
                option.value = selectedSubregionType[option.id] || false;
              }
            });
          });

          $scope.$watchGroup([
            'height',
            'width',
            'toLeftEdge',
            'toBottomEdge',
          ], () => {
            if (!$scope.subregion) {
              return;
            }
            $scope.subregion.xMin = convertFromUserUnits($scope.toLeftEdge);
            $scope.subregion.xMax = convertFromUserUnits($scope.toLeftEdge + $scope.width);
            $scope.subregion.yMin = convertFromUserUnits($scope.toBottomEdge);
            $scope.subregion.yMax = convertFromUserUnits($scope.toBottomEdge + $scope.height);
          });

          $scope.$watch('subregion', () => {
            if (!$scope.subregion) {
              return;
            }

            $scope.subregionForSchema = angular.copy($scope.subregion);
            Object.keys($scope.subregionForSchema).forEach(key => {
              const mapping = PROPS_MAPPING.find(el => el.id === key);
              $scope.subregionForSchema[mapping.botProperty] = $scope.subregionForSchema[key];
              delete $scope.subregionForSchema[key];
            });
          }, true);

          $scope.$on('$destroy', () => {
            $scope.dataProvider.subregionToEdit = undefined;
            $scope.subregion = undefined;
            $scope.subregionForSchema = undefined;
          });
        }

        /**
         * Checking form fields emptiness.
         * @returns boolean
         */
        function checkEmpty() {
          return !$scope.subregion ||
            $scope.subregion.xMin == null ||
            $scope.subregion.xMax == null ||
            $scope.subregion.yMin == null ||
            $scope.subregion.yMax == null ||
            !$scope.subregion.subregionName ||
            !$scope.selectedSubregionType;
        }

        function initViewModel() {
          let deviceInfo = angular.copy($scope.dataProvider.deviceResource.value);
          let regionBehaviors = angular.copy($scope.dataProvider.locationStateRegionBehaviorsResource.value) || [];
          $scope.roomBoundaries = angular.copy($scope.dataProvider.locationStateBoundariesResource.value);
          let subregion = angular.copy($scope.dataProvider.subregionToEdit);
          $scope.subregions = angular.copy($scope.dataProvider.locationStateRegionsResource.value);

          $scope.occupants = (angular.copy($scope.dataProvider.locationUsersResource.value) || [])
            .map((user) => ({
              id: user.id,
              name: userNameFilter(user)
            }));
          $scope.selectedSubregionOccupantIds = subregion?.['occupant_ids'] || [];

          $scope.mountingType = $scope.roomBoundaries['mounting_type'];

          if (isNaN(deviceInfo.goalId)) {
            $scope.noDeviceGoal = true;
            $scope.editable = false;
            return;
          }

          const behaviors = regionBehaviors
            // Filter by goal ID
            .filter((subregionType) => {
              let compatibleBehaviors = subregionType.compatible_behaviors;
              if (compatibleBehaviors && compatibleBehaviors.length > 0) {
                return compatibleBehaviors.some(cb => cb == deviceInfo.goalId);
              }
              return true;
            })
            // Filter by mounting type
            .filter((subregionType) => {
              const requiredMountingTypes = subregionType.required_mounting_types;
              if (requiredMountingTypes && requiredMountingTypes.length > 0) {
                return requiredMountingTypes.some(mt => mt == $scope.mountingType);
              }
              return true;
            })
            // Filter by device type
            .filter((subregionType) => {
              const supportedDeviceTypeIds = subregionType.supported_device_type_ids;
              if (supportedDeviceTypeIds && supportedDeviceTypeIds.length > 0 && deviceInfo.type != null) {
                return supportedDeviceTypeIds.some(dt => dt == deviceInfo.type);
              }
              return true;
            });

          $scope.editable = !!$scope.dataProvider.botDrivenRegionsResource.value && deviceInfo.connected;
          $scope.behaviors = behaviors;

          const subregionType = (subregion && subregion.context_id != null && behaviors)
            ? behaviors.find(b => b.context_id === subregion.context_id)
            : undefined;

          initForm();
          processProps(subregion, subregionType);

          $scope.toLeftEdge = convertToUserUnits($scope.subregion.xMin);
          $scope.toBottomEdge = convertToUserUnits($scope.subregion.yMin);
          $scope.width = convertToUserUnits(Math.abs(($scope.subregion.xMax || 0) - ($scope.subregion.xMin || 0)));
          $scope.height = convertToUserUnits(Math.abs(($scope.subregion.yMax || 0) - ($scope.subregion.yMin || 0)));
        }

        /**
         * Process properties mapping from location state.
         */
        function processProps(subregion, subregionType) {
          let result = {};

          if ($scope.isNewSubregion || !subregion) {
            result.uniqueId = utils.generateUUID();
          } else {
            PROPS_MAPPING.forEach(el => {
              result[el.id] = subregion[el.botProperty];
            });

            // Initially process options
            $scope.options.map((option) => {
              option.editable = subregionType ? !!subregionType[option.editProperty] : false;
              option.value = subregionType ? !!subregion[option.id] || false : false;
            });
          }

          $scope.subregion = result;
        }

        function initFormRules() {
          $.fn.form.settings.rules.numberBetween = (value, n) => {
            let flag = $.fn.form.settings.rules.number(value);
            if (flag) {
              if (n) {
                let arr = n.split('..');
                let min = arr.length >= 1 ? Number(arr[0]) : Number.MIN_VALUE,
                  max = arr.length >= 2 ? Number(arr[1]) : Number.MAX_VALUE,
                  valNum = Number(value) && parseFloat(value);
                return valNum >= min & valNum <= max;
              } else {
                return true;
              }
            }
            return false;
          };
          $.fn.form.settings.rules.inRoom = () => {
            // Check if subregion fits inside the room
            if (!$scope.width || !$scope.height || $scope.toLeftEdge == null || $scope.toBottomEdge == null) {
              return false;
            }

            const xMin = convertFromUserUnits($scope.toLeftEdge);
            const xMax = convertFromUserUnits($scope.toLeftEdge + $scope.width);
            const yMin = convertFromUserUnits($scope.toBottomEdge);
            const yMax = convertFromUserUnits($scope.toBottomEdge + $scope.height);

            return xMin >= $scope.roomBoundaries.x_min_meters &&
              yMin >= $scope.roomBoundaries.y_min_meters &&
              xMax <= $scope.roomBoundaries.x_max_meters &&
              yMax <= $scope.roomBoundaries.y_max_meters;
          };
        }

        /**
         * Initialize form validation rules.
         */
        function initForm() {
          const roomWidth = convertToUserUnits(Math.abs($scope.roomBoundaries?.x_max_meters - $scope.roomBoundaries?.x_min_meters) || 4);
          const roomHeight = convertToUserUnits(Math.abs($scope.roomBoundaries?.y_max_meters - $scope.roomBoundaries?.y_min_meters) || 4);

          $('#subregion-edit-form').form({
            inline: true,
            fields: {
              'name': {
                rules: [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  }
                ]
              },
              'behavior': {
                rules: [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  }
                ]
              },

              'width': {
                rules: [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  },
                  {
                    type: `numberBetween[${convertToUserUnits(subregionMinSize)}..${roomWidth}]`,
                    prompt: $t('regions.boundaries.prompt.between', {
                      min: convertToUserUnits(subregionMinSize),
                      max: roomWidth.toFixed(1),
                    })
                  },
                ]
              },
              'height': {
                rules: [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  },
                  {
                    type: `numberBetween[${convertToUserUnits(subregionMinSize)}..${roomHeight}]`,
                    prompt: $t('regions.boundaries.prompt.between', {
                      min: convertToUserUnits(subregionMinSize),
                      max: roomHeight.toFixed(1),
                    })
                  },
                ]
              },

              'toLeftEdge': {
                rules: [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  },
                  {
                    type: 'inRoom',
                    prompt: $t('regions.boundaries.prompt.inRoom')
                  },
                ]
              },
              'toBottomEdge': {
                rules: [
                  {
                    type: 'empty',
                    prompt: $t('regions.boundaries.prompt.empty')
                  },
                  {
                    type: 'inRoom',
                    prompt: $t('regions.boundaries.prompt.inRoom')
                  },
                ]
              },
            }
          });
        }

        function switchOption(option) {
          if ($scope.loading || !option || !option.editable) {
            return;
          }
          let index = $scope.options.findIndex(el => el.id === option.id);
          $scope.options[index].value = !option.value;
        }

        function onSaveButtonClick() {
          $('#subregion-edit-form').form('validate form');
          if (!$('#subregion-edit-form').form('is valid')) {
            return;
          }

          $scope.editable = false;
          const data = angular.copy($scope.subregion);

          Object.keys(data).map(key => {
            let i = PROPS_MAPPING.findIndex(el => el.id === key);
            data[PROPS_MAPPING[i].botProperty] = data[key];
            delete data[key];
          });

          // Process options
          $scope.options.forEach((option) => {
            data[option.id] = option.value;
          });

          // Process occupants
          data.occupant_ids = ($scope.selectedSubregionOccupantIds && $scope.selectedSubregionOccupantIds.length > 0)
            ? angular.copy($scope.selectedSubregionOccupantIds)
            : null;

          return $scope.dataProvider.saveRadarSubregion(data, $scope.isNewSubregion)
            .then(() => {
              ToastsService.success();
              $scope.dataProvider.activeTab = 'subregions';
            }, (err) => {
              $scope.editable = true;
              utils.handleApiError(err);
            });
        }

        function onCancelButtonClick() {
          $scope.dataProvider.subregionToEdit = undefined;
          $scope.subregion = undefined;
          $scope.dataProvider.activeTab = $scope.dataProvider.previousTab || 'subregions';
        }

        /**
         * Convert meters to user units (centimeters or inches)
         * @param {number} meters
         * @returns {number}
         */
        function convertToUserUnits(meters) {
          if ($scope.systemOfMeasurement === 'metric') {
            return Math.round(meters * 1000) / 10; // from meters to centimeters
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertMetersToInches(meters); // from meters to inches
          }
          return meters;
        }

        /**
         * Convert user units (centimeters or inches) to meters
         * @param {number} usersUnits
         * @returns {number}
         */
        function convertFromUserUnits(usersUnits) {
          if ($scope.systemOfMeasurement === 'metric') {
            return usersUnits / 100; // from centimeters to meters
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertInchesToMeters(usersUnits, 4); // from inches to meters
          }
          return usersUnits;
        }

        /**
         * Convert value to the opposite units of measurement and format value
         * @param {number} value value in current user units of measurement (centimeters or inches)
         * @returns {string} value in opposite user units of measurement (inches or centimeters)
         */
        function convertToOppositeUnits(value) {
          if (!value) {
            value = 0;
          }

          if ($scope.systemOfMeasurement === 'metric') {
            return utils.convertMetersToInches(value / 100, 1) + ' in'; // from centimeters to inches
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertInchesToMeters(value * 100, 2) + ' cm'; // from inches to centimeters
          }
          return value;
        }

        function onSubregionSchemaChange(subregionCoordinates) {
          if (!subregionCoordinates || !$scope.subregion) {
            return;
          }

          $scope.toLeftEdge = convertToUserUnits(subregionCoordinates.xMin);
          $scope.toBottomEdge = convertToUserUnits(subregionCoordinates.yMin);
          $scope.width = convertToUserUnits(subregionCoordinates.width);
          $scope.height = convertToUserUnits(subregionCoordinates.height);
        }

      }
    };
  })
  .name;
